import { useTranslation } from "react-i18next";
import { Box, Checkbox, ClickAwayListener, Collapse, Divider, IconButton, InputBase, ListItemIcon, ListItemText, MenuItem, Paper, Popper, Select, Stack, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useCallback, useMemo, useState } from "react";
import { setLocalStorageValue } from "../../../../common/helpers";
import { TaskEnums, TaskEnumsLists } from "../../../../common/constants";
import { FilterListOff as ClearIcon, Sort as SortIcon, Search as SeachIcon, Close as CloseIcon, FilterList as FilterIcon } from "@mui/icons-material";
import { useDispatch } from "../providers/DispatchProvider";
import UrgencyToolbar from "./UrgencyToolbar";


const useStyles = makeStyles()(
  (theme) => ({
    root: {
      position: "relative",
      marginTop: theme.spacing(-0.5),
      height: "40px",
      overflow: "visible",
    },
    scrollbox: {
      zIndex: 99,
      position: "absolute",
      top: 0,
      left: "-16px",
      height: "100%",
      width: "100vw",
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      overflowY: "hidden",
      overflowX: "auto",
      "scrollbarWidth": "none",
      "&::-webkit-scrollbar": {
        display: "none"
      }
    },
    searchBox: {
      background: theme.palette.background.default,
      borderRadius: "19px",
      height: "38px",
      minWidth: "38px",
      width: "auto",
      overflow: "hidden",
      paddingLeft: theme.spacing(0.25)
    },
    searchInput: {
      width: "160px",
      marginLeft: theme.spacing(1.75),
      fontSize: "14px"
    },
    searchActive: {
      minWidth: "200px",
      paddingRight: theme.spacing(0.5)
    },
    iconOnlySelect: {
      background: theme.palette.background.default,
      border: "none !important",
      outline: "none !important",
      height: "38px",
      minWidth: "46px",
      maxWidth: "46px",
      borderRadius: theme.spacing(2.5),
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    select: {
      background: theme.palette.background.default,
      border: "none !important",
      outline: "none !important",
      height: "38px",
      maxWidth: "50vw",
      borderRadius: theme.spacing(2.5),
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    selectIconContainer: {
      display: "flex",
      alignItems: "center",
      color: theme.palette.text.primary,
    },
    selectIcon: {
      marginLeft: "-2px",
      marginBottom: "-2px",
      fontSize: "22px",
      color: theme.palette.text.primary
    },
    selectIconActive: {
      marginLeft: "-2px",
      marginBottom: "-2px",
      fontSize: "22px",
      color: theme.palette.primary.contrast
    },
    selectActive: {
      background: theme.palette.primary.main,
      color: `${theme.palette.primary.contrast} !important`
    },
    filterPopperContainer: {
      background: theme.palette.background.default,
      borderRadius: "50%",
      height: "38px"
    },
    popper: {
      zIndex: 9999
    },
    popperPaper: {
      maxWidth: "460px",
      borderRadius: theme.spacing(2)
    },
    filterPopupsActive: {
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrast,
      "&:hover": {
        background: theme.palette.primary.main
      }
    }
  })
);

export default function DispatchCompactToolbar(props) {
  const { popper, searchDefaultOpen } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();

  const {
    urgencyFilter,
    setUrgencyFilter,
    planningFilter,
    planningFilters,
    setPlanningFilter,
    lowPriorityFilter,
    setLowPriorityFilter,
    normalPriorityFilter,
    setNormalPriorityFilter,
    highPriorityFilter,
    setHighPriorityFilter,
    searchFilter,
    setSearchFilter,
    orderTasksBy,
    setOrderTasksBy
  } = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);

  const openPopper = useCallback((ev) => {
    setAnchorEl(ev.target.closest("button") || ev.target);
  }, []);

  const closePopper = useCallback(ev => {
    setAnchorEl(null);
  }, [])

  const popperOpen = Boolean(anchorEl);


  const onSetOrderTasksBy = useCallback((ev) => { setOrderTasksBy(ev.target.value); setLocalStorageValue("orderTasksByV2", ev.target.value) }, []);

  const onSetPriorityFilter = useCallback((ev) => {
    const value = typeof ev.target.value === "string" ? ev.target.value.split(",") : ev.target.value;

    if (value.includes("reset")) {
      setLowPriorityFilter(true);
      setNormalPriorityFilter(true);
      setHighPriorityFilter(true);
    } else {
      setLowPriorityFilter(value.includes(TaskEnums.Priority.Low))
      setNormalPriorityFilter(value.includes(TaskEnums.Priority.Normal))
      setHighPriorityFilter(value.includes(TaskEnums.Priority.High))
    }
  }, [])

  const priorityValues = useMemo(() => {
    return [
      lowPriorityFilter ? TaskEnums.Priority.Low : null,
      normalPriorityFilter ? TaskEnums.Priority.Normal : null,
      highPriorityFilter ? TaskEnums.Priority.High : null,
    ].filter(v => v !== null);
  }, [lowPriorityFilter, normalPriorityFilter, highPriorityFilter])

  const renderPriorityValue = useCallback((selected) => {
    return selected.length === 3 ?
      t("task:urgency:priority") :
      selected.length === 0 ?
        t("common:none") :
        selected.map(value => t(`task:enums:priority:${TaskEnumsLists.Priority[value]}`)).join(", ");
  }, [t]);


  const renderUrgencyValue = useCallback(() => {
    return urgencyFilter === null ? t("task:urgency:estimatedUrgency") : t(`task:enums:estimatedUrgencyAlternate:${TaskEnumsLists.EstimatedUrgency[urgencyFilter]}`);
  }, [urgencyFilter, t]);


  const renderPlanningValue = useCallback(() => {
    return planningFilter === -1 ? t("common:allTime") : planningFilters[planningFilter].label;
  }, [planningFilter, planningFilters, t]);


  const [searchActive, setSearchActive] = useState(false);

  const closeSearch = useCallback((ev) => {
    setSearchActive(false);
    setSearchFilter("");
  }, [searchDefaultOpen]);

  const openSearch = useCallback(() => {
    if (!searchDefaultOpen) {
      setSearchActive(true);
    }
  }, [searchDefaultOpen]);

  const urgentFilterActive = planningFilter !== -1 || urgencyFilter !== null || !lowPriorityFilter || !normalPriorityFilter || !highPriorityFilter;

  return (
    <Box className={classes.root}>

      <Stack className={classes.scrollbox} direction="row" spacing={1}>


        <Stack className={`${classes.searchBox} ${searchActive || searchDefaultOpen ? classes.searchActive : ""}`} direction="row" justifyContent="space-between" alignItems="center">

          {
            searchActive || searchDefaultOpen  ? (
              <InputBase
                autoFocus
                placeholder={t("common:search")}
                className={classes.searchInput}
                value={searchFilter}
                onChange={ev => setSearchFilter(ev.target.value)}
              />
            ) : null
          }

          <IconButton size="small" onClick={searchActive || searchDefaultOpen ? closeSearch : openSearch} color="primary">
            {searchActive || (searchDefaultOpen && !!searchFilter ) ? <CloseIcon /> : <SeachIcon />}
          </IconButton>
        </Stack>


        <Select
          size="small"
          variant="outlined"
          value={orderTasksBy}
          onChange={onSetOrderTasksBy}
          className={`${classes.iconOnlySelect} ${orderTasksBy !== "urgency" ? classes.selectActive : ""}`}
          renderValue={() => (
            <div className={classes.selectIconContainer}>
              <SortIcon fontSize="small" className={orderTasksBy !== "urgency" ? classes.selectIconActive : classes.selectIcon} />
            </div>
          )}
          IconComponent={() => null}
        >
          <MenuItem value="urgency">{t("common:filters:orderBy:urgency")}</MenuItem>
          <Divider />
          <MenuItem value="new">{t("common:filters:orderBy:new")}</MenuItem>
          <MenuItem value="old">{t("common:filters:orderBy:old")}</MenuItem>
          <MenuItem value="state_asc">{t("common:filters:orderBy:state_asc")}</MenuItem>
          <MenuItem value="state_desc">{t("common:filters:orderBy:state_desc")}</MenuItem>
          <MenuItem value="driver">{t("common:filters:orderBy:driver")}</MenuItem>
          <MenuItem value="eta">{t("common:filters:orderBy:eta")}</MenuItem>
        </Select>

        {popper ?
          (
            <Box className={classes.filterPopperContainer}>
              <IconButton color="primary" onClick={popperOpen ? closePopper : openPopper} className={urgentFilterActive ? classes.filterPopupsActive : undefined}>
                {popperOpen ? <CloseIcon /> : <FilterIcon />}
              </IconButton>
              <Popper
                className={classes.popper}
                open={popperOpen}
                anchorEl={anchorEl}
                placement="bottom-start"
              >
                <Paper elevation={3} className={classes.popperPaper}>
                  <Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ padding: 1, paddingLeft: 2 }}>
                      <Typography variant="body2" fontWeight={500}>{t("common:filtersTitle")}</Typography>
                      <IconButton color="primary" onClick={closePopper} size="small">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Divider />
                    <UrgencyToolbar compactMode />

                  </Stack>

                </Paper>
              </Popper>
            </Box>
          ) : (
            <>
              <Select
                size="small"
                variant="outlined"
                className={`${classes.select} ${urgencyFilter !== null ? classes.selectActive : ""}`}
                onChange={ev => setUrgencyFilter(ev.target.value === "" ? null : ev.target.value)}
                value={urgencyFilter === null ? "" : urgencyFilter}
                renderValue={renderUrgencyValue}
                displayEmpty
              >
                <MenuItem value={""}>
                  <ListItemIcon><ClearIcon /></ListItemIcon>
                  <ListItemText>{t("common:all")}</ListItemText>
                </MenuItem>
                <Divider />
                {TaskEnumsLists.EstimatedUrgency.map((value, key) => (
                  <MenuItem value={key} key={key}>
                    {t(`task:enums:estimatedUrgencyAlternate:${value}`)}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                variant="outlined"
                className={`${classes.select} ${planningFilter !== -1 ? classes.selectActive : ""}`}
                onChange={ev => setPlanningFilter(ev.target.value)}
                value={planningFilter}
                renderValue={renderPlanningValue}
                displayEmpty
              >
                <MenuItem value={-1}>
                  <ListItemIcon><ClearIcon /></ListItemIcon>
                  <ListItemText>{t("common:allTime")}</ListItemText>
                </MenuItem>
                <Divider />
                {planningFilters.map((filter, value) => <MenuItem value={value} key={value}>{filter.label}</MenuItem>)}
              </Select>

              <Select
                size="small"
                variant="outlined"
                value={priorityValues}
                onChange={onSetPriorityFilter}
                renderValue={renderPriorityValue}
                className={`${classes.select} ${priorityValues.length !== 3 ? classes.selectActive : ""}`}
                displayEmpty
                multiple
              >
                <MenuItem value="reset">
                  <ListItemIcon><ClearIcon /></ListItemIcon>
                  <ListItemText>{t("common:all")}</ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem value={TaskEnums.Priority.Low}>
                  <Checkbox checked={lowPriorityFilter} />
                  <ListItemText primary={t(`task:enums:priority:Low`)} />
                </MenuItem>

                <MenuItem value={TaskEnums.Priority.Normal}>
                  <Checkbox checked={normalPriorityFilter} />
                  <ListItemText primary={t(`task:enums:priority:Normal`)} />
                </MenuItem>

                <MenuItem value={TaskEnums.Priority.High}>
                  <Checkbox checked={highPriorityFilter} />
                  <ListItemText primary={t(`task:enums:priority:High`)} />
                </MenuItem>
              </Select>
            </>
          )}

      </Stack>
    </Box>
  );
}
